import { Button, Select, SelectItem, Skeleton, Spinner } from '@heroui/react';
import { SquareDashed } from 'iconoir-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import type { AgentExecutionDetail } from '~/api/agentLogs/types';
import i18n from '~/common/helpers/i18n';
import { useAgentLogDatasets } from '~/scenes/Monitor/scenes/AgentLogs/hooks/useAgentLogDatasets';
import {
  isDatasetLockError,
  useCollectAgentExecution
} from '~/scenes/Monitor/scenes/AgentLogs/hooks/useCollectAgentExecution';

type AgentLogCollectPanelProps = {
  realm: string;
  environment: string;
  detail: AgentExecutionDetail;
  onClose: () => void;
  onSuccess?: () => void;
};

export function AgentLogCollectPanel({
  realm,
  environment,
  detail,
  onClose,
  onSuccess
}: AgentLogCollectPanelProps) {
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(
    null
  );

  const { data: datasets = [], isLoading: datasetsLoading } =
    useAgentLogDatasets({
      realm,
      pipelineName: detail.pipelineName,
      pipelineVersion: detail.pipelineVersion,
      connectorId: detail.connectorId,
      enabled: true
    });

  const { mutateAsync: collectExecution, isPending } = useCollectAgentExecution(
    {
      realm,
      environment,
      detail,
      datasetId: selectedDatasetId
    }
  );

  const handleConfirm = async () => {
    if (!selectedDatasetId) {
      return;
    }

    try {
      await collectExecution();
      toast.success(i18n.t('scenes.monitor.agentLogs.collect.success'));
      onSuccess?.();
      onClose();
    } catch (error) {
      if (isDatasetLockError(error)) {
        toast.error(i18n.t('scenes.monitor.agentLogs.errors.datasetLocked'));
        return;
      }

      toast.error(i18n.t('scenes.monitor.agentLogs.errors.generic'));
    }
  };

  return (
    <div
      className='flex h-full min-w-0 flex-1 flex-col border-l border-outline bg-surface'
      data-testid='monitor-agent-logs-collect-panel'
    >
      <div className='flex flex-1 flex-col gap-6 overflow-auto px-8 py-7'>
        <div className='flex flex-col gap-2'>
          <h3 className='text-headline-sm font-semibold text-on-surface'>
            {i18n.t('scenes.monitor.agentLogs.collect.createTestCase')}
          </h3>
          <p className='text-body-regular-md text-on-surface-variant'>
            {i18n.t('scenes.monitor.agentLogs.collect.description')}
          </p>
        </div>

        <Select
          label={i18n.t('scenes.monitor.agentLogs.collect.selectDataset')}
          placeholder={i18n.t(
            'scenes.monitor.agentLogs.collect.selectDatasetPlaceholder'
          )}
          selectedKeys={selectedDatasetId ? [selectedDatasetId] : []}
          onSelectionChange={keys => {
            const value = Array.from(keys)[0];
            setSelectedDatasetId(value ? String(value) : null);
          }}
          isLoading={datasetsLoading}
          data-testid='monitor-agent-logs-collect-dataset-select'
        >
          {datasets.map(dataset => (
            <SelectItem key={dataset.id} textValue={dataset.name}>
              {dataset.name}
            </SelectItem>
          ))}
        </Select>

        {!selectedDatasetId ? (
          <div
            className='flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center'
            data-testid='monitor-agent-logs-collect-empty'
          >
            <SquareDashed className='icon-large text-on-surface-variant' />
            <p className='max-w-md text-body-regular-md text-on-surface-variant'>
              {i18n.t('scenes.monitor.agentLogs.collect.emptyState')}
            </p>
          </div>
        ) : null}
      </div>

      <div className='flex items-center justify-end gap-3 border-t border-outline px-8 py-6'>
        <Button
          variant='light'
          onPress={onClose}
          data-testid='monitor-agent-logs-collect-cancel'
        >
          {i18n.t('scenes.monitor.agentLogs.collect.cancel')}
        </Button>
        <Button
          color='primary'
          onPress={handleConfirm}
          isDisabled={!selectedDatasetId || isPending}
          isLoading={isPending}
          data-testid='monitor-agent-logs-collect-confirm'
        >
          {i18n.t('scenes.monitor.agentLogs.collect.confirm')}
        </Button>
      </div>
    </div>
  );
}

export function AgentLogCollectPanelSkeleton() {
  return (
    <div className='flex min-w-[600px] flex-1 flex-col gap-4 border-l border-outline px-8 py-7'>
      <Skeleton className='h-8 w-48 rounded' />
      <Skeleton className='h-4 w-full rounded' />
      <Skeleton className='h-10 w-full rounded' />
      <div className='flex flex-1 items-center justify-center'>
        <Spinner />
      </div>
    </div>
  );
}
